import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TasksPage from './pages/TasksPage';
import CutsPage from './pages/CutsPage';
import VerifyPage from './pages/VerifyPage';
import CorrectPage from './pages/CorrectPage';
import CompletedPage from './pages/CompletedPage';
import { deleteOrphanSegments, getQueueCounts, subscribeToDb, type QueueCounts } from './db';
import type { PageProps, TabId } from './pages/types';

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'tasks', label: 'Tasks', icon: '🎧' },
  { id: 'cuts', label: 'Cuts', icon: '✂️' },
  { id: 'verify', label: 'Verify', icon: '✅' },
  { id: 'correct', label: 'Correct', icon: '✏️' },
  { id: 'done', label: 'Done', icon: '📦' },
];

const EMPTY_COUNTS: QueueCounts = { cuts: 0, pendingVerify: 0, toCorrect: 0, completed: 0 };

/** Which count, if any, a tab shows as a badge. */
function badgeFor(tab: TabId, counts: QueueCounts): number {
  if (tab === 'cuts') return counts.cuts;
  if (tab === 'verify') return counts.pendingVerify;
  if (tab === 'correct') return counts.toCorrect;
  return 0;
}

/**
 * App shell: renders the active page and the fixed bottom tab bar.
 *
 * No router on purpose — four tabs, one screen each. Two pieces of state are
 * lifted here because they cross tab boundaries:
 *
 *  - `focusTaskId`: which task a hand-off pointed at ("verify THIS task",
 *    "open THIS transcript"). Pages treat it as a hint, never a filter — the
 *    Verify and Correct queues stay cross-task, the focused task just sorts
 *    to the front.
 *  - `visit`: a per-tab nonce used as the page `key`. Every page snapshots its
 *    queue on mount, so remounting on tab entry is what keeps the queues fresh.
 *    Conditional rendering already unmounts the old page; the key makes the
 *    "re-read on entry" contract explicit and survives a future refactor to a
 *    router or keep-alive shell.
 */
export default function App() {
  const [tab, setTab] = useState<TabId>('tasks');
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const [visit, setVisit] = useState(0);
  const [counts, setCounts] = useState<QueueCounts>(EMPTY_COUNTS);

  /** Mirrors `tab` for the callbacks below, which are created once. */
  const tabRef = useRef<TabId>('tasks');

  /** Switch tabs, forcing the destination page to remount and re-read. */
  const goToTab = useCallback((next: TabId) => {
    setFocusTaskId(null);
    setTab(next);
    // Re-entering the tab you're already on must NOT remount: it would throw
    // away the page's session state (the Verify cursor and its undo) for a
    // queue that cannot have changed behind your back.
    if (next !== tabRef.current) setVisit((v) => v + 1);
    tabRef.current = next;
  }, []);

  /** Switch tabs AND point the destination page at one specific task. */
  const openTask = useCallback((taskId: string, next: TabId) => {
    setFocusTaskId(taskId);
    setTab(next);
    // always remount, even within the same tab — the focus has to be applied
    setVisit((v) => v + 1);
    tabRef.current = next;
  }, []);

  /* ---- tab-bar badges, kept live off the db change signal ---- */

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ---- one-shot startup maintenance: sweep segments whose task is gone ---- */
  useEffect(() => {
    void deleteOrphanSegments()
      .then((n) => {
        if (n > 0) console.warn(`startup maintenance: removed ${n} orphaned segment(s)`);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let queued = false;

    const refresh = () => {
      if (queued) return;
      queued = true;
      // coalesce the burst of writes a bulk insert or a reconcile sweep fires
      queueMicrotask(() => {
        queued = false;
        void getQueueCounts()
          .then((next) => {
            if (mountedRef.current) setCounts(next);
          })
          .catch(() => undefined);
      });
    };

    refresh();
    return subscribeToDb(refresh);
  }, []);

  const pageProps: PageProps = useMemo(
    () => ({ onGoToTab: goToTab, onOpenTask: openTask, focusTaskId }),
    [goToTab, openTask, focusTaskId],
  );

  return (
    <div className="app">
      <main className="app__main">
        {tab === 'tasks' && <TasksPage key={`tasks-${visit}`} {...pageProps} />}
        {tab === 'cuts' && <CutsPage key={`cuts-${visit}`} {...pageProps} />}
        {tab === 'verify' && <VerifyPage key={`verify-${visit}`} {...pageProps} />}
        {tab === 'correct' && <CorrectPage key={`correct-${visit}`} {...pageProps} />}
        {tab === 'done' && <CompletedPage key={`done-${visit}`} {...pageProps} />}
      </main>

      <nav className="tabbar" aria-label="Main">
        {TABS.map((t) => {
          const badge = badgeFor(t.id, counts);
          return (
            <button
              key={t.id}
              type="button"
              className="tabbar__btn"
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => goToTab(t.id)}
            >
              <span className="tabbar__icon" aria-hidden="true">
                {t.icon}
              </span>
              <span>
                {t.label}
                {badge > 0 && <span className="visually-hidden">, {badge} waiting</span>}
              </span>
              {badge > 0 && (
                <span className="tabbar__badge" aria-hidden="true">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
